// ABOUTME: Attachments tab content for ticket details panel in service desk.
// ABOUTME: Displays categorized attachments (images, documents, other) from chat messages; click opens the photo viewer or downloads the file.

import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "../../../../../../components/theme/text/Text";
import MPhotoViewer from "../../../../../../components/global/Modals/MPhotoViewer";
import { downloadFile } from "../../../../../../utils/downloadFile";

const ATTACHMENT_CATEGORIES = {
    IMAGES: 'images',
    DOCUMENTS: 'documents',
    OTHER: 'other',
};

const DetailsTabAttachments = ({ messages, colors, theme }) => {
    const { t } = useTranslation();
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    const categorizedAttachments = useMemo(() => {
        const attachments = {
            [ATTACHMENT_CATEGORIES.IMAGES]: [],
            [ATTACHMENT_CATEGORIES.DOCUMENTS]: [],
            [ATTACHMENT_CATEGORIES.OTHER]: [],
        };

        messages.forEach(msg => {
            if (msg.content.type === 'attachments') {
                msg.content.attachments.forEach(att => {
                    const category = att.type === 'image'
                        ? ATTACHMENT_CATEGORIES.IMAGES
                        : att.type === 'document' || att.type === 'pdf'
                            ? ATTACHMENT_CATEGORIES.DOCUMENTS
                            : ATTACHMENT_CATEGORIES.OTHER;

                    attachments[category].push({
                        ...att,
                        senderName: msg.senderName,
                        timestamp: msg.timestamp,
                    });
                });
            }
        });

        return attachments;
    }, [messages]);

    // Image attachments in the format the photo viewer expects
    const photosForViewer = useMemo(() =>
        categorizedAttachments[ATTACHMENT_CATEGORIES.IMAGES].map((att, index) => ({
            id: index,
            url: att.url,
            fileName: att.fileName,
        })),
    [categorizedAttachments]);

    // Keep the viewer in range when a refetch shrinks the message list
    useEffect(() => {
        if (photoViewerOpen && currentPhotoIndex >= photosForViewer.length) {
            if (photosForViewer.length === 0) {
                setPhotoViewerOpen(false);
                setCurrentPhotoIndex(0);
            } else {
                setCurrentPhotoIndex(photosForViewer.length - 1);
            }
        }
    }, [photoViewerOpen, currentPhotoIndex, photosForViewer.length]);

    const handleImageClick = (index) => {
        setCurrentPhotoIndex(index);
        setPhotoViewerOpen(true);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Images category */}
            {categorizedAttachments[ATTACHMENT_CATEGORIES.IMAGES].length > 0 && (
                <Box>
                    <Text semibold sx={{ mb: 1.5 }}>{t("images")}</Text>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {categorizedAttachments[ATTACHMENT_CATEGORIES.IMAGES].map((att, index) => (
                            <Box
                                key={index}
                                onClick={() => handleImageClick(index)}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                }}
                            >
                                <img
                                    src={att.url}
                                    alt={att.fileName}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Documents category */}
            {categorizedAttachments[ATTACHMENT_CATEGORIES.DOCUMENTS].length > 0 && (
                <Box>
                    <Text semibold sx={{ mb: 1.5 }}>{t("documents")}</Text>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {categorizedAttachments[ATTACHMENT_CATEGORIES.DOCUMENTS].map((att, index) => (
                            <Box
                                key={index}
                                onClick={() => downloadFile(att.url, att.fileName)}
                                sx={{
                                    height: 40,
                                    borderRadius: 3,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                    px: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                            >
                                <Text fontSize={13}>{att.fileName}</Text>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Other category */}
            {categorizedAttachments[ATTACHMENT_CATEGORIES.OTHER].length > 0 && (
                <Box>
                    <Text semibold sx={{ mb: 1.5 }}>{t("other")}</Text>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {categorizedAttachments[ATTACHMENT_CATEGORIES.OTHER].map((att, index) => (
                            <Box
                                key={index}
                                onClick={() => downloadFile(att.url, att.fileName)}
                                sx={{
                                    height: 40,
                                    borderRadius: 3,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                    px: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                            >
                                <Text fontSize={13}>{att.fileName}</Text>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <MPhotoViewer
                open={photoViewerOpen}
                onClose={() => setPhotoViewerOpen(false)}
                photos={photosForViewer}
                currentPhotoIndex={currentPhotoIndex}
                onPhotoChange={setCurrentPhotoIndex}
                showDownload
            />
        </Box>
    );
};

export default DetailsTabAttachments;
